import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme definitions
export const THEMES = {
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Original dark fantasy football theme',
    colors: {
      primary: '#10B981',
      primaryHover: '#059669',
      primarySubtle: 'rgba(16, 185, 129, 0.1)',
      secondary: '#3B82F6',
      background: '#0A1118',
      surface: '#1A1F25',
      surfaceSecondary: '#0F1218',
      surfaceHover: '#242A32',
      border: '#2D3643',
      borderSecondary: '#374151',
      text: '#FFFFFF',
      textSecondary: '#9CA3AF',
      textMuted: '#6B7280',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6'
    }
  },
  dark: {
    id: 'dark',
    name: 'Dark',
    description: 'Pure dark theme with high contrast',
    colors: {
      primary: '#3B82F6',
      primaryHover: '#2563EB',
      primarySubtle: 'rgba(59, 130, 246, 0.1)',
      secondary: '#0EA5E9',
      background: '#000000',
      surface: '#111111',
      surfaceSecondary: '#0A0A0A',
      surfaceHover: '#1A1A1A',
      border: '#333333',
      borderSecondary: '#444444',
      text: '#FFFFFF',
      textSecondary: '#CCCCCC',
      textMuted: '#999999',
      success: '#10B981',
      warning: '#FFA500',
      error: '#FF4444',
      info: '#0EA5E9'
    }
  },
  light: {
    id: 'light',
    name: 'Light',
    description: 'Clean light theme with excellent readability',
    colors: {
      primary: '#16A34A',
      primaryHover: '#15803D',
      primarySubtle: 'rgba(22, 163, 74, 0.1)',
      secondary: '#2563EB',
      background: '#FFFFFF',
      surface: '#F8FAFC',
      surfaceSecondary: '#F1F5F9',
      surfaceHover: '#E2E8F0',
      border: '#D1D5DB',
      borderSecondary: '#9CA3AF',
      text: '#111827',
      textSecondary: '#374151',
      textMuted: '#6B7280',
      success: '#10B981',
      warning: '#D97706',
      error: '#DC2626',
      info: '#2563EB'
    }
  }
};


// Create the context
const ThemeContext = createContext();

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme provider component
export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('classic');

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('footy-draft-theme');
    if (savedTheme && THEMES[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  // Change theme
  const changeTheme = (themeId) => {
    if (THEMES[themeId]) {
      setCurrentTheme(themeId);
      localStorage.setItem('footy-draft-theme', themeId);
    }
  };

  // Get current theme
  const getCurrentTheme = () => {
    return THEMES[currentTheme];
  };

  // Apply theme to CSS variables
  useEffect(() => {
    const theme = getCurrentTheme();
    const root = document.documentElement;

    // Remove any existing theme classes
    root.classList.remove('theme-classic', 'theme-dark', 'theme-light');
    
    // Add current theme class
    root.classList.add(`theme-${currentTheme}`);

    // Apply color variables
    Object.entries(theme.colors).forEach(([key, value]) => {
      const cssVar = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVar, value);
    });

    // Set body background to ensure full coverage
    document.body.style.backgroundColor = theme.colors.background;
    document.body.style.color = theme.colors.text;

  }, [currentTheme]);

  const value = {
    // Current state
    currentTheme,
    theme: getCurrentTheme(),
    
    // Available options
    availableThemes: THEMES,
    
    // Actions
    changeTheme,
    
    // Utilities
    getCurrentTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;